import { getApi } from "../../../helpers/auth_helpers";
import { masterIdFromRef, masterNameFromRef } from "../../../uploads/productDraftApi";

/** One pick fills every classification level the form has: artform › type › category › sub-category › weave. */
export type CategoryPath = {
  key: string;
  artform: { value: string; label: string };
  type: { value: string; label: string };
  category: { value: string; label: string };
  subCategory: { value: string; label: string };
  weaveType?: { value: string; label: string };
};

const RECENT_KEY = "hkp.product.recentCategoryPaths";
const base = () => String(process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "");

const opt = (raw: unknown) => ({ value: masterIdFromRef(raw), label: masterNameFromRef(raw) });

/** A sub-category or weave-type row (both carry their parents) → a full path, or null if a level is missing. */
export const pathFromRow = (row: any, kind: "subcategory" | "weavetype"): CategoryPath | null => {
  if (!row) return null;
  const sub = kind === "weavetype" ? opt(row.subcategory) : { value: masterIdFromRef(row._id ?? row.id), label: row.name || "" };
  const path: CategoryPath = {
    key: "",
    artform: opt(row.artform),
    type: opt(row.type),
    category: opt(row.category),
    subCategory: sub,
    weaveType: kind === "weavetype" ? { value: masterIdFromRef(row._id ?? row.id), label: row.name || "" } : undefined,
  };
  const levels = [path.artform, path.type, path.category, path.subCategory, ...(path.weaveType ? [path.weaveType] : [])];
  if (levels.some((level) => !level.value || !level.label)) return null;
  path.key = levels.map((level) => level.value).join(">");
  return path;
};

/** "Saree › Flower butidaar motifs › Kadhua" — the part people recognise first. */
export const pathTitle = (p: CategoryPath): string =>
  [p.category.label, p.subCategory.label, p.weaveType?.label].filter(Boolean).join(" › ");

/** "Handloom › Apparel woven textiles" */
export const pathParents = (p: CategoryPath): string => `${p.artform.label} › ${p.type.label}`;

/** Weave matches first (they fill all 5 levels), then sub-categories; no duplicates. */
export const mergePathResults = (weaves: any[], subs: any[]): CategoryPath[] => {
  const seen = new Set<string>();
  const out: CategoryPath[] = [];
  const push = (p: CategoryPath | null) => {
    if (p && !seen.has(p.key)) {
      seen.add(p.key);
      out.push(p);
    }
  };
  (weaves || []).forEach((row) => push(pathFromRow(row, "weavetype")));
  (subs || []).forEach((row) => push(pathFromRow(row, "subcategory")));
  return out;
};

const docsOf = (res: any): any[] => (Array.isArray(res?.data?.docs) ? res.data.docs : Array.isArray(res?.data) ? res.data : []);

export const searchCategoryPaths = async (query: string): Promise<CategoryPath[]> => {
  const q = encodeURIComponent(query.trim());
  // Weave types filter by `label`, sub-categories and categories by `name`.
  const [weaves, subs, categories] = await Promise.all([
    getApi(`${base()}/weaveType/weaveTypes?offset=0&limit=12&label=${q}`).catch(() => null),
    getApi(`${base()}/subcategory/subcategories?offset=0&limit=12&name=${q}`).catch(() => null),
    getApi(`${base()}/category/categories?offset=0&limit=3&name=${q}`).catch(() => null),
  ]);
  // "saree" names a category: offer the sub-categories under it ("Saree › Jangla").
  const subsUnderCategories = await Promise.all(
    docsOf(categories).slice(0, 3).map((row) =>
      getApi(`${base()}/subcategory/subcategories?offset=0&limit=8&categoryId=${encodeURIComponent(masterIdFromRef(row._id ?? row.id))}`)
        .then(docsOf)
        .catch(() => [])
    )
  );
  const subRows = docsOf(subs);
  // "butid" should also offer the weaves under a matching sub-category ("… › Flower butidaar › Kadhua").
  const underSubs = await Promise.all(
    subRows.slice(0, 4).map((row) =>
      getApi(`${base()}/weaveType/weaveTypes?offset=0&limit=8&subCategoryId=${encodeURIComponent(masterIdFromRef(row._id ?? row.id))}`)
        .then(docsOf)
        .catch(() => [])
    )
  );
  // Full 5-level paths first (named weaves, then weaves under matching sub-categories), then
  // sub-categories (named ones, then those under a matching category).
  const weaveRows = [...filterRows(docsOf(weaves), query), ...underSubs.flat()];
  return mergePathResults(weaveRows, [...subRows, ...subsUnderCategories.flat()]).slice(0, 12);
};

const filterRows = (rows: any[], query: string) => {
  const q = query.trim().toLowerCase();
  return rows.filter((row) => String(row?.name || "").toLowerCase().includes(q));
};

/** Keep paths where some level contains every typed word (the list endpoints don't always filter by name). */
export const filterPaths = (paths: CategoryPath[], query: string): CategoryPath[] => {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return paths;
  return paths.filter((p) => {
    const hay = [p.artform, p.type, p.category, p.subCategory, p.weaveType]
      .map((level) => (level?.label || "").toLowerCase())
      .join(" ");
    return words.every((w) => hay.includes(w));
  });
};

export const readRecentPaths = (): CategoryPath[] => {
  try {
    const rows = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(rows) ? rows.filter((row) => row && row.key).slice(0, 3) : [];
  } catch {
    return [];
  }
};

export const rememberPath = (path: CategoryPath) => {
  try {
    const next = [path, ...readRecentPaths().filter((row) => row.key !== path.key)].slice(0, 3);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Private mode or full storage: recents are only a convenience.
  }
};
